import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "./api";
import { setGoogleMapsApiKey } from "./googleMaps";
import VERIFIED_BUSINESS from "../data/verifiedBusiness.json";
import { getBuildSnapshot } from "./buildSnapshot";
import { GA4_ID } from "./analytics";

const SiteConfigContext = createContext(null);

const FALLBACK = {
    company_name: "VVPSS Pvt. Ltd. Taxi Services",
    tagline: "Trusted Taxi & Tempo Traveller Rental in Satna",
    phone: "+919425712055",
    phone_display: "+91 94257 12055",
    phone_alt_1: "+91 96857 64656",
    phone_alt_2: "+91 94249 73323",
    whatsapp: "919425712055",
    email: "vvpsspvtltdtaxiservices@gmail.com",
    address: "VVPSS Pvt. Ltd. Taxi Services, Satna, Madhya Pradesh – 485001, India",
    city: "Satna",
    state: "Madhya Pradesh",
    pincode: "485001",
    country: "India",
    business_hours: "24 x 7 Available",
    founded_year: "2010",
    facebook_url: "https://www.facebook.com/share/14FcywhEDUM/",
    youtube_url: "https://www.youtube.com/@VVPSSPVT.LTD.TaxiServices",
    ...VERIFIED_BUSINESS,
};

/**
 * Test-placeholder IDs that must never fire in production. Some deploys
 * historically carried "G-TEST123" / "AW-TEST12345" / "GTM-TEST99" in DB
 * Site Settings; we hard-reject these client-side so a stale DB entry
 * cannot ship duplicate/broken tracking, even before an admin cleans it up.
 */
function looksLikeTestId(id) {
    if (!id || typeof id !== "string") return true;
    const v = id.trim();
    if (!v) return true;
    // Explicit known bad values + any TEST-containing pattern in the ID body.
    if (/^(G|GTM|AW)-TEST/i.test(v)) return true;
    if (/TEST/i.test(v) && /^(G|GTM|AW)-/i.test(v)) return true;
    return false;
}

function injectGA4(id) {
    if (id === GA4_ID) return; // The supplied property has one owner: the shared document head.
    if (looksLikeTestId(id) || typeof document === "undefined") return;
    if (document.querySelector(`script[data-ga4="${id}"]`)) return;
    const s1 = document.createElement("script");
    s1.async = true;
    s1.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
    s1.dataset.ga4 = id;
    document.head.appendChild(s1);
    const s2 = document.createElement("script");
    s2.dataset.ga4Init = "true";
    s2.text = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}window.gtag=gtag;gtag('js',new Date());gtag('config','${id}');`;
    document.head.appendChild(s2);
}

function injectGTM(id) {
    if (looksLikeTestId(id) || typeof document === "undefined") return;
    if (document.querySelector(`script[data-gtm="${id}"]`)) return;
    const s = document.createElement("script");
    s.dataset.gtm = id;
    s.text = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${id}');`;
    document.head.appendChild(s);
}

function injectAdsConversion(id) {
    if (looksLikeTestId(id) || typeof document === "undefined") return;
    // Ads uses the same gtag; ensure config for the AW id
    if (document.querySelector(`script[data-adsconv="${id}"]`)) return;
    const s = document.createElement("script");
    s.dataset.adsconv = id;
    s.text = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}window.gtag=gtag;gtag('js',new Date());gtag('config','${id}');`;
    document.head.appendChild(s);
}

export function SiteConfigProvider({ children }) {
    const [config, setConfig] = useState(() => ({ ...FALLBACK, ...getBuildSnapshot().config, ...VERIFIED_BUSINESS }));

    useEffect(() => {
        let mounted = true;
        api.getSiteConfig()
            .then((data) => {
                if (mounted && data) {
                    setConfig({ ...FALLBACK, ...data, ...VERIFIED_BUSINESS });
                    if (data.google_maps_api_key) setGoogleMapsApiKey(data.google_maps_api_key);

                    // Tracking rollout rule: if a real GTM container is configured
                    // we let GTM own GA4 + Ads tags (configured inside the GTM UI)
                    // and skip the direct gtag/GA4/Ads injections here to avoid
                    // duplicate events. If GTM is absent we fall back to direct
                    // GA4 + Ads gtag snippets so tracking still works.
                    const gtmActive = !looksLikeTestId(data.gtm_id);
                    if (gtmActive) {
                        injectGTM(data.gtm_id);
                    } else {
                        if (data.google_analytics_id) injectGA4(data.google_analytics_id);
                        if (data.ads_conversion_id) injectAdsConversion(data.ads_conversion_id);
                    }
                    if (data.google_site_verification) {
                        // Overlay the meta tag Googlebot reads during JS rendering.
                        let el = document.head.querySelector('meta[name="google-site-verification"]');
                        if (!el) {
                            el = document.createElement("meta");
                            el.setAttribute("name", "google-site-verification");
                            document.head.appendChild(el);
                        }
                        el.setAttribute("content", data.google_site_verification);
                    }
                }
            })
            .catch(() => {
                /* keep fallback */
            });
        return () => {
            mounted = false;
        };
    }, []);

    return (
        <SiteConfigContext.Provider value={config}>
            {children}
        </SiteConfigContext.Provider>
    );
}

export const useSiteConfig = () => {
    const ctx = useContext(SiteConfigContext);
    return ctx || FALLBACK;
};
